<template>
  <div v-click-away="close" class="dropdown" :class="{ 'dropdown-opened': opened }">
    <button
      :class="{ active: opened, disabled: !_options.length && !hasMainSlot }"
      @click.prevent="toggle"
    >
      <span>{{ label }}</span>
    </button>
    <ul v-show="opened" ref="menu" :class="customClass" @click="opened ? close() : null">
      <template v-if="hasMainSlot">
        <slot name="dropdownMenu" />
      </template>
      <template v-else>
        <li v-for="(option, key) in _options" :key="key">
          <a
            v-if="option.href"
            :href="option.href"
            :title="option.title"
            :target="option.target"
            :class="option.customClass.length ? option.customClass : ''"
            @click="option.click($event)"
          >
            {{ option.text }}
          </a>
          <span
            v-else
            @click="option.click($event)"
          >
            {{ option.text }}
          </span>
        </li>
      </template>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'Dropdown',
  props: {
    label: {
      type: String,
      default: () => '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    positionStrategy: {
      type: String,
      default: 'absolute',
      validator: (v) => [ 'absolute', 'fixed' ].includes(v),
    },
    align: {
      type: String,
      default: 'start',
      validator: (v) => [ 'start', 'end' ].includes(v),
    },
    customClass: {
      type: String,
      default: '',
    },
    fitAnchorWidth: {
      type: Boolean,
      default: false,
    },
    scrollIntoContainer: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      opened: false,
      scroller: null,
    };
  },
  computed: {
    _options() {
      return this.options.map((option) => {
        const {
          title = '',
          text = '',
          href = false,
          click = () => {},
          hidden = false,
          target = '',
          customClass = '',
        } = option;

        return {
          title: title || text,
          text,
          href,
          click,
          hidden,
          target,
          customClass,
        };
      }).filter((option) => option.hidden);
    },
    hasMainSlot() {
      return this.hasSlot('dropdownMenu');
    },
  },
  mounted() {
    this.scroller = this.$el.closest('div[data-pc-section="tablecontainer"]') ?? null;
  },
  methods: {
    async toggle() {
      this.opened = !this.opened;
      await this.$nextTick();

      const td = this.$el.closest('td[data-p-frozen-column="true"]');
      if (td) {
        td.classList.toggle('dropdown-host-open', this.opened);
      }

      if (!this.opened) {
        this.removeListeners();
        this.resetMenuStyles();
        return;
      }

      if (this.positionStrategy !== 'fixed') {
        this.stickTop();
        return;
      }
      this.applyFixedPosition();
      this.addListeners();
    },
    close() {
      this.opened = false;

      this.removeListeners();
      this.resetMenuStyles();

      const td = this.$el.closest('td[data-p-frozen-column="true"]');
      if (td) {
        td.classList.remove('dropdown-host-open');
      }
    },
    applyFixedPosition() {
      const btn = this.$el.querySelector('button');
      const { menu } = this.$refs;

      if (!btn || !menu) {
        return;
      }

      menu.classList.add('dropdown-fixed');

      if (this.fitAnchorWidth) {
        const br = btn.getBoundingClientRect();
        menu.style.setProperty('--dropdown-anchor-width', `${Math.round(br.width)}px`);
      } else {
        menu.style.removeProperty('--dropdown-anchor-width');
      }

      menu.style.left = '0px';
      menu.style.top = '0px';
      const br = btn.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const mw = menu.offsetWidth;
      const mh = menu.offsetHeight;

      let left;
      if (this.align === 'end') {
        left = br.right - mw;
      } else {
        left = br.left;
      }

      left = Math.max(8, Math.min(left, vw - mw - 8));

      let top = br.bottom + 8;
      if (top + mh > vh - 8) top = Math.max(8, br.top - 8 - mh);

      menu.style.left = `${Math.round(left)}px`;
      menu.style.top = `${Math.round(top)}px`;
    },
    addListeners() {
      this.reposition = () => {
        if (this.opened) {
          this.applyFixedPosition();
        }
      };

      window.addEventListener('resize', this.reposition);
      window.addEventListener('scroll', this.reposition, true);

      this.scroller?.addEventListener('scroll', this.reposition, { passive: true });
    },
    removeListeners() {
      window.removeEventListener('resize', this.reposition);
      window.removeEventListener('scroll', this.reposition, true);

      this.scroller?.removeEventListener('scroll', this.reposition);
    },
    resetMenuStyles() {
      const { menu } = this.$refs;
      if (!menu) {
        return;
      }

      menu.classList.remove('dropdown-fixed');
      menu.classList.remove('stick-top');

      Object.assign(menu.style, { left: '', top: '' });
    },
    stickTop() {
      const bounds = this.$refs.menu.getBoundingClientRect();
      if ((bounds.height + bounds.top) > window.innerHeight) {
        this.$refs.menu.classList.add('stick-top');
      } else {
        this.$refs.menu.classList.remove('stick-top');
      }
    },
    hasSlot(slot) {
      return !!this.$slots[slot];
    },
  },
};
</script>
